/**
 * Chiamate al core per la firma a più passi (contratto, clausole vessatorie,
 * privacy...) con OTP opzionale. Anonime: autenticate dal token del link di firma.
 * Vedi flusia-core src/domains/documents/handlers/signingFlowHandler.ts.
 */
const CORE_URL = import.meta.env.VITE_CORE_URL || 'https://core.flusia.it';
const CORE_DEV_KEY = import.meta.env.VITE_GATEWAY_DEV_KEY as string | undefined;

export interface SigningStep {
  key: string;
  title: string;
  text: string | null;
}

export interface SigningPlan {
  steps: SigningStep[];
  signed: { key: string; signedAt: string; signerName: string | null }[];
  otp: {
    mode: 'off' | 'optional' | 'required';
    channel: 'email' | 'sms';
    destination: string | null;
    verified: boolean;
    available: boolean;
  };
}

/** La firma a passi serve se il modello ha più punti di firma o chiede l'OTP. */
export const isMultiStepPlan = (p: SigningPlan | null): p is SigningPlan =>
  !!p && (p.steps.length > 1 || p.otp.mode !== 'off');

async function call<T>(path: string, body: Record<string, unknown>): Promise<T> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (CORE_DEV_KEY) headers['X-Flusia-Dev-Key'] = CORE_DEV_KEY;
  const res = await fetch(`${CORE_URL}/api/documents/quote/public/signing/${path}`, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json?.error?.message || 'Operazione non riuscita, riprova');
  return json as T;
}

export const fetchSigningPlan = (signatureToken: string) =>
  call<SigningPlan>('plan', { signatureToken });

export const sendSigningOtp = (signatureToken: string) =>
  call<{ sent: boolean; destination: string; devCode?: string }>('otp/send', { signatureToken });

export const verifySigningOtp = (signatureToken: string, code: string) =>
  call<{ verified: boolean }>('otp/verify', { signatureToken, code });

export const signStep = (
  signatureToken: string,
  stepKey: string,
  signatureDataUrl: string,
  signerName: string,
) =>
  call<{ signed: boolean; stepKey: string; signedAt: string }>('step', {
    signatureToken,
    stepKey,
    signatureDataUrl,
    signerName,
    accepted: true,
  });
